import React, { Fragment, useMemo } from 'react';
import { Skeleton } from '@material-ui/lab';
import { removeXSSAttacks } from '../../../common/helper';
import { convertIntoEmail } from '../../../utils/emailTemplates';

const NotesHeading = ({
  type = 'Notes',
  top = 0,
}) => (
  <div
    className={`
      flex
      align-items-center
      justify-content-flex-start
      height-38
      p-l-3
      p-r-3
      bg-lighter-grey
      sticky
    `}
    style={{ top }}
  >
    <div className='flex flex-fill align-items-center'>
      <div className='min-width-165 max-width-165 font-roboto font-14 m-r-2'>Date</div>
      <div className='notes-header font-roboto font-14'>{type}</div>
    </div>
  </div>
);

const NoteUI = ({
  colorCode = '',
  date = '',
  text = '',
}) => (
  <div className={`
    flex
    align-items-center
    justify-content-flex-start
    min-height-38
    p-l-3
    p-r-3
    p-t-2
    p-b-2
    bg-lightest-primary
  `}
    style={{
      backgroundColor: colorCode,
    }}
  >
    <div className='flex flex-fill align-items-center'>
      <div className='min-width-165 max-width-165 font-roboto font-14 m-r-2'>{date}</div>
      <div
        className='notes-header font-roboto font-14'
        dangerouslySetInnerHTML={{
          __html: removeXSSAttacks(convertIntoEmail(text)),
        }}
      />
    </div>
  </div>
);

const NoteLoading = () => (
  <div className={`
    flex
    align-items-center
    justify-content-flex-start
    min-height-38
    p-l-3
    p-r-3
    p-t-2
    p-b-2
    bg-lightest-primary
  `}
  >
    <div className='flex flex-fill align-items-center'>
      <Skeleton variant='text' className='width-165 m-r-2' />
      <div className='notes-header font-roboto font-14'>
        <Skeleton variant='text' className='min-width-165' />
      </div>
    </div>
  </div>
);

const NoNoteUI = ({
  type = '',
}) => (
  <div className={`
    flex
    align-items-center
    justify-content-flex-start
    min-height-38
    p-l-3
    p-r-3
    p-t-2
    p-b-2
    bg-lightest-primary
  `}
  >
    <div className='flex flex-gap-1 flex-fill align-items-center'>
      <div>No</div>
      <div>{type.toLowerCase()}</div>
    </div>
  </div>
);

const NotesWrapper = ({
  stickyIndex = 0,
  type = 'Notes',
  notes = [],
  api = { data: {}, called: false, loading: false },
}) => {
  const UICollection = new Map([
    ['true-true-true', <NoteLoading />],
    ['true-true-false', <NoteLoading />],
    ['true-false-true', (
      notes.map(({ NoteId, Colorcode, EntryDate, Text }) => (
        <NoteUI
          key={NoteId}
          colorCode={Colorcode}
          date={EntryDate}
          text={Text}
        />
      ))
    )],
    ['true-false-false', <NoNoteUI type={type} />],
    ['false-true-true', <NoteLoading />],
    ['false-true-false', <NoteLoading />],
    ['false-false-true', <NoteLoading />],
    ['false-false-false', <NoteLoading />],
  ]);

  return (
    <Fragment>
      <NotesHeading top={stickyIndex * 38} type={type} />
      {
        UICollection.get(`${api.called}-${api.loading}-${notes.length > 0}`)
      }
    </Fragment>
  );
};

const prepareMO = ({
  _notes,
  _notesAPI,
}) => {
  const notesMO = _notes?.medical?.order?.OrderNotes || [];
  return {
    key: 'mo',
    type: 'Medical Order Notes',
    notes: notesMO,
    api: _notesAPI?.medical?.order,
  };
};

const prepareMP = ({
  _notes,
  _notesAPI,
}) => {
  const notesMP = _notes?.medical?.patient?.OrderNotes || [];
  return {
    key: 'mp',
    type: 'Medical Patient Notes',
    notes: notesMP,
    api: _notesAPI?.medical?.patient,
  };
};

const prepareO = ({
  _notes,
  _notesAPI,
}) => {
  const notesO = _notes?.order?.OrderNotes || [];
  return {
    key: 'o',
    type: 'Order Notes',
    notes: notesO,
    api: _notesAPI?.order,
  };
};

const prepareP = ({
  _notes,
  _notesAPI,
}) => {
  const notesP = _notes?.patient?.PatientNotes || [];
  return {
    key: 'p',
    type: 'Patient Notes',
    notes: notesP,
    api: _notesAPI?.patient,
  };
};

const NoteGenerator = new Map([
  ['mo', prepareMO],
  ['mp', prepareMP],
  ['o', prepareO],
  ['p', prepareP],
]);

export const Notes = ({
  tabKey = '',
  selectedKey = '',
  data: {
    notes: _notes = {},
    notesAPI: _notesAPI = {},
    notesToDisplay = [
      { key: 'mo', },
      { key: 'mp', },
      { key: 'o', },
      { key: 'p', },
    ],
  },
}) => {
  const notes = useMemo(() => {
    return notesToDisplay.map(({ key }) => {
      const prepare = NoteGenerator.get(key);
      return prepare({ _notes, _notesAPI });
    });
  }, [_notes, _notesAPI]);

  const displayClass = tabKey !== selectedKey ? 'd-none' : '';

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      <div className='flex flex-column flex-fill flex-gap-2'>
        {
          notes.map(({ key, type, notes, api }, index) => (
            <NotesWrapper key={key} stickyIndex={index} type={type} notes={notes} api={api} />
          ))
        }
      </div>
    </div>
  );
};

export default Notes;
